import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Button } from './ui';

interface Log {
  id: string;
  timestamp: string;
  level: string;
  message: string;
  user?: string;
  endpoint?: string;
  traceback?: string;
  method?: string;
  path?: string;
  module?: string;
  function?: string;
  line?: number;
}

interface LogResponse {
  logs: Log[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

const LogViewer: React.FC = () => {
  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState(false);
  const [level, setLevel] = useState('ERROR');
  const [hours, setHours] = useState(24);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const [isProduction, setIsProduction] = useState(true);
  const { apiRequest } = useAuth();

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        level,
        hours: hours.toString(),
        search,
        page: page.toString(),
        pageSize: '100'
      });
      
      const response = await apiRequest<LogResponse>({
        url: `/ui/admin/logs?${params}`,
        method: 'GET'
      });
      
      setLogs(response.logs || []);
      setTotal(response.total || 0);
    } catch (error: any) {
      console.error('Failed to fetch logs:', error);
      setLogs([]);
      setTotal(0);
      // If it's a 403 error (not production), we might want to show a message
      if (error?.response?.status === 403 || error?.status === 403) {
        console.log('Log viewer only available in production environment');
        setIsProduction(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [level, hours, page]);

  const toggleLogExpansion = (logId: string) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedLogs(newExpanded);
  };

  const getLogLevelClass = (logLevel: string): string => {
    switch (logLevel.toUpperCase()) {
      case 'ERROR':
        return 'log-error';
      case 'WARNING':
        return 'log-warning';
      case 'INFO':
        return 'log-info';
      default:
        return '';
    }
  };

  return (
    <div className="log-viewer">
      <PageHeader title="System Logs (Production Only)" />
      
      {!isProduction ? (
        <div style={{
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          padding: '20px',
          margin: '20px 0',
          textAlign: 'center',
          color: '#6c757d'
        }}>
          <h3>Log Viewer Not Available</h3>
          <p>The system log viewer is only available in production environments for security reasons.</p>
          <p>In development, logs are output to the console.</p>
        </div>
      ) : (
        <>
      
      <div className="filters">
        <select 
          value={level} 
          onChange={(e) => {
            setLevel(e.target.value);
            setPage(1);
          }}
          className="filter-select"
        >
          <option value="ALL">All Levels</option>
          <option value="ERROR">Errors</option>
          <option value="WARNING">Warnings</option>
        </select>
        
        <select 
          value={hours} 
          onChange={(e) => {
            setHours(Number(e.target.value));
            setPage(1);
          }}
          className="filter-select"
        >
          <option value={1}>Last Hour</option>
          <option value={24}>Last 24 Hours</option>
          <option value={168}>Last Week</option>
        </select>
        
        <input
          type="text"
          placeholder="Search logs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              setPage(1);
              fetchLogs();
            }
          }}
          className="search-input"
        />
        
        <Button
          variant="primary"
          onClick={() => {
            setPage(1);
            fetchLogs();
          }}
        >
          Search
        </Button>
      </div>
      
      {loading ? (
        <div className="loading">Loading logs...</div>
      ) : (
        <>
          <div className="log-list">
            {logs.length === 0 ? (
              <div className="no-logs">No logs found matching your criteria</div>
            ) : (
              logs.map(log => (
                <div 
                  key={log.id} 
                  className={`log-entry ${getLogLevelClass(log.level)}`}
                >
                  <div className="log-header">
                    <span className="timestamp">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                    <span className={`level ${getLogLevelClass(log.level)}`}>
                      {log.level}
                    </span>
                    {log.user && <span className="user">User: {log.user}</span>}
                    {log.endpoint && <span className="endpoint">{log.endpoint}</span>}
                    {log.method && <span className="method">{log.method}</span>}
                    {log.traceback && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => toggleLogExpansion(log.id)}
                      >
                        {expandedLogs.has(log.id) ? 'Hide' : 'Show'} Traceback
                      </Button>
                    )}
                  </div>
                  <div className="log-message">{log.message}</div>
                  {log.module && (
                    <div className="log-details">
                      <span className="module">Module: {log.module}</span>
                      {log.function && <span className="function">Function: {log.function}</span>}
                      {log.line && <span className="line">Line: {log.line}</span>}
                    </div>
                  )}
                  {log.traceback && expandedLogs.has(log.id) && (
                    <pre className="log-traceback">{log.traceback}</pre>
                  )}
                </div>
              ))
            )}
          </div>
          
          <div className="pagination">
            <Button
              variant="primary"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className="pagination-info">
              Page {page} of {Math.ceil(total / 100) || 1}
            </span>
            <Button
              variant="primary"
              onClick={() => setPage(p => p + 1)}
              disabled={page * 100 >= total}
            >
              Next
            </Button>
          </div>
        </>
      )}
      
      <style>{`
        .log-viewer {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .log-viewer h2 {
          margin-bottom: 20px;
          color: #333;
        }

        .filters {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }

        .filter-select {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background-color: white;
          cursor: pointer;
        }

        .search-input {
          flex: 1;
          min-width: 200px;
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
        }

        .search-button {
          padding: 8px 16px;
          background-color: #007bff;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .search-button:hover {
          background-color: #0056b3;
        }

        .loading, .no-logs {
          text-align: center;
          padding: 40px;
          color: #666;
        }

        .log-list {
          margin-bottom: 20px;
        }

        .log-entry {
          border: 1px solid #ddd;
          border-radius: 4px;
          padding: 12px;
          margin-bottom: 10px;
          background-color: white;
        }

        .log-entry.log-error {
          border-left: 4px solid #dc3545;
        }

        .log-entry.log-warning {
          border-left: 4px solid #ffc107;
        }

        .log-entry.log-info {
          border-left: 4px solid #17a2b8;
        }

        .log-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 8px;
          flex-wrap: wrap;
        }

        .timestamp {
          color: #666;
          font-size: 14px;
        }

        .level {
          padding: 2px 8px;
          border-radius: 3px;
          font-size: 12px;
          font-weight: bold;
          text-transform: uppercase;
        }

        .level.log-error {
          background-color: #dc3545;
          color: white;
        }

        .level.log-warning {
          background-color: #ffc107;
          color: #333;
        }

        .level.log-info {
          background-color: #17a2b8;
          color: white;
        }

        .user, .endpoint, .method {
          font-size: 14px;
          color: #666;
        }

        .expand-button {
          padding: 2px 8px;
          background-color: #6c757d;
          color: white;
          border: none;
          border-radius: 3px;
          font-size: 12px;
          cursor: pointer;
        }

        .expand-button:hover {
          background-color: #5a6268;
        }

        .log-message {
          margin-bottom: 8px;
          color: #333;
        }

        .log-details {
          display: flex;
          gap: 15px;
          font-size: 12px;
          color: #666;
          margin-bottom: 8px;
        }

        .log-traceback {
          background-color: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          padding: 10px;
          margin-top: 10px;
          overflow-x: auto;
          font-family: monospace;
          font-size: 12px;
          line-height: 1.5;
        }

        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 15px;
          margin-top: 20px;
        }

        .pagination-button {
          padding: 8px 16px;
          background-color: #007bff;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .pagination-button:hover:not(:disabled) {
          background-color: #0056b3;
        }

        .pagination-button:disabled {
          background-color: #6c757d;
          cursor: not-allowed;
        }

        .pagination-info {
          color: #666;
        }
      `}</style>
      </>
      )}
    </div>
  );
};

export default LogViewer;